/**
 * TemplateListItem — Einzelner Listeneintrag fuer die Vorlagen-Profil-Verwaltung
 * (Phase 8 Plan 03).
 *
 * Zeigt: Profil-Name + Stern-Icon wenn Default + ⋮-Menue (Default, Loeschen).
 * Aktiv: gelber Hintergrund (#E9FF57 / #1A1A1A)
 * Inaktiv: hover:bg-gray-100
 *
 * Last-Template-Block (D-06): Loeschen-Button ist disabled wenn `isLastInStage`
 * gesetzt ist (Tooltip erklaert warum). Auto-Workflow braucht mindestens eine
 * Vorlage pro Stufe — der Block stellt sicher dass Mails immer noch rauskoennen.
 */
import { useState } from 'react';
import { Star, MoreVertical } from 'lucide-react';
import type { LeadTemplateProfile } from '../../types';

interface Props {
  profile: LeadTemplateProfile;
  isActive: boolean;
  isLastInStage: boolean;
  onSelect: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}

export function TemplateListItem({
  profile,
  isActive,
  isLastInStage,
  onSelect,
  onSetDefault,
  onDelete,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      data-profile-id={profile.id}
      className={`relative flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm select-none transition-colors
        ${isActive
          ? 'bg-[#E9FF57] text-[#1A1A1A] font-semibold border-l-4 border-[#1A1A1A] pl-2'
          : 'text-[#1A1A1A] hover:bg-gray-100 border-l-4 border-transparent pl-2'}`}
      onClick={onSelect}
    >
      <span className="flex-1 truncate">{profile.name}</span>
      {profile.isDefault && (
        <Star
          className="w-3 h-3 shrink-0 text-[#1A1A1A]"
          fill="currentColor"
          aria-label="Default-Vorlage"
        />
      )}
      <button
        type="button"
        className="shrink-0 p-0.5 rounded hover:bg-black/10"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Optionen"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {menuOpen && (
        <div
          className="absolute right-0 top-8 z-20 w-56 bg-white border border-[#DAD9D9] rounded shadow-lg py-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            disabled={profile.isDefault}
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => {
              setMenuOpen(false);
              onSetDefault();
            }}
          >
            Als Default markieren
          </button>
          <button
            type="button"
            disabled={isLastInStage}
            title={
              isLastInStage
                ? 'Letzte Vorlage dieser Stage darf nicht geloescht werden — Auto-Workflow braucht mindestens eine Vorlage pro Stufe.'
                : undefined
            }
            className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            onClick={() => {
              setMenuOpen(false);
              onDelete();
            }}
          >
            Loeschen{isLastInStage ? ' (letzte Vorlage)' : ''}
          </button>
        </div>
      )}
    </div>
  );
}
